/* ═══════════════════════════════════════════════════════════════════════
   Dirty-review reader — the join between the engine's flagged items and the
   DOM docx-preview renders. Pure functions, no React, so the mapping can be
   proven against a real formatted .docx in a test (dirty-reader-mapping.test.ts)
   instead of being taken on faith.

   The engine hands every flagged item a `bodyParagraphIndex`: its 0-based
   ordinal among the body's TOP-LEVEL paragraphs, counted from the body CONTENT
   start (the "PART 1 - GENERAL" boundary) with tables skipped.

   docx-preview renders the WHOLE file, so the rendered paragraph list ALSO
   contains the injected pre-body title block that sits above that boundary.
   The join is therefore

       renderedParagraphs[item.bodyParagraphIndex + offset]

   where `offset` is how many rendered paragraphs sit above the body content
   start. That offset is MEASURED, never assumed — see resolveParagraphOffset.
   ══════════════════════════════════════════════════════════════════════ */

import { formatCounter } from '../../shared/body-layout';
import type { DirtyItem, LevelPreview } from '../../shared/forma';

/** breakPages is OFF, deliberately (2026-08-22).

    docx-preview's `splitBySection` SPLITS a paragraph in two when it contains
    a page break and breakPages is on — precisely the paragraph the page-break
    items point at. That would inject a phantom paragraph into the rendered
    flow and shift every `bodyParagraphIndex` after it, silently
    mis-highlighting the rest of the document. Correct mapping beats page
    cosmetics, so the document renders as one continuous page.

    Headers and footers still render — the formatter formats them, so they
    must be reviewable — and land in <header>/<footer> elements, never in the
    <article> body flow the mapping walks. */
export const READER_RENDER_OPTIONS = {
  className: 'docx',
  inWrapper: true,
  breakPages: false,
  ignoreLastRenderedPageBreak: true,
  renderHeaders: true,
  renderFooters: true,
  renderFootnotes: true,
  renderEndnotes: true,
  renderChanges: false,
  renderComments: false,
  experimental: false,
  debug: false,
  useBase64URL: true,
};

export const normalizeReaderText = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

/** The engine caps item text at 140 chars with a trailing ellipsis, so a long
    paragraph is matched on its prefix. */
export function readerTextMatches(paraText: string, itemText: string): boolean {
  const a = normalizeReaderText(paraText);
  const b = normalizeReaderText(itemText);
  if (!a || !b) return false;
  if (b.endsWith('…')) return a.startsWith(normalizeReaderText(b.slice(0, -1)));
  return a === b;
}

/** Mirrors `findBodyContentStart`'s own window (word-engine.ts
    MAX_PREBODY_TEXT_PARAS) — a genuine PART/GENERAL/PRODUCTS/EXECUTION opener
    is only believed within this many text-bearing paragraphs of the body
    start. Not imported (word-engine.ts pulls in node-only deps that have no
    business in the renderer bundle) — kept in sync by the test that proves a
    LATE bare marker can never win (dirty-reader-mapping.test.ts). */
const MAX_PREBODY_TEXT_PARAS = 40;

/** Mirrors `findBodyContentStart`'s PART-marker rule against rendered text —
    the fallback anchor for a document whose flagged items are all blanks and
    breaks and so carry no text to match on.

    Bounded to the same early window the engine uses (2026-09-04, "01 21 00 -
    Allowances.docx" / "02 00 00 - Existing Conditions.docx"): an unbounded
    search let a late, ordinary ARTICLE heading that happens to end in
    "EXECUTION" or "PRODUCTS" pose as the PART-1 boundary, scoring negative
    and starving `resolveParagraphOffset` of any usable candidate. The engine
    itself never accepts such a marker past this window either — it falls
    back to the first HeadingN paragraph instead, which is exactly what
    `headingFallbackParagraphIndex` below now also offers as a candidate. */
function markerParagraphIndex(paraTexts: string[]): number | null {
  let textParasSeen = 0;
  for (let i = 0; i < paraTexts.length; i++) {
    const s = paraTexts[i].replace(/\s+/g, ' ').trim();
    if (!s) continue;
    if (textParasSeen <= MAX_PREBODY_TEXT_PARAS) {
      if (/^PART\s*\d/i.test(s)) return i;
      if (s.length < 200 && /(?:^|\s)(GENERAL|PRODUCTS|EXECUTION)$/i.test(s)) return i;
    }
    textParasSeen++;
  }
  return null;
}

/** Mirrors `findBodyContentStart`'s HeadingN fallback (word-engine.ts
    `headingFallback`): the first rendered paragraph that carries an outline
    level at all, used when the document has NO bare PART/GENERAL/etc. opener
    anywhere — its body simply starts at the first Heading-styled paragraph.
    `levels` is `renderedParagraphLevel` pre-computed over every rendered
    paragraph, in the same order as `paraTexts`. */
function headingFallbackParagraphIndex(levels: (number | null)[]): number | null {
  for (let i = 0; i < levels.length; i++) {
    if (levels[i] != null) return i;
  }
  return null;
}

/**
 * How many rendered paragraphs sit above the body content start, or null when
 * no offset can be trusted.
 *
 * Candidates come from (a) the flagged items' own text, matched anywhere in
 * the rendered document, (b) the engine's bounded PART-marker rule, and (c)
 * the engine's HeadingN fallback — the boundary a document with no bare
 * marker at all actually uses. Every candidate is then scored against EVERY
 * item — a text item must land on matching text, a blank item must land on a
 * blank paragraph — and the best score wins. A candidate that scores nothing
 * is rejected, which is what makes a stale document (a read-only reopen
 * after fixes were written) degrade to "no highlights" rather than "wrong
 * highlights".
 *
 * `levels` is every rendered paragraph's outline level (`renderedParagraphLevel`),
 * same order as `paraTexts` — optional only so call sites that genuinely have
 * no level data (rare; every real caller has the elements in hand) still
 * compile. Omitting it just drops candidate (c).
 */
export function resolveParagraphOffset(
  paraTexts: string[],
  items: DirtyItem[],
  levels: (number | null)[] = []
): number | null {
  const mapped = items.filter((i) => typeof i.bodyParagraphIndex === 'number');
  if (mapped.length === 0 || paraTexts.length === 0) return null;

  const candidates = new Set<number>([0]);
  const marker = markerParagraphIndex(paraTexts);
  if (marker != null) candidates.add(marker);
  const headingFallback = headingFallbackParagraphIndex(levels);
  if (headingFallback != null) candidates.add(headingFallback);
  for (const anchor of mapped) {
    if (normalizeReaderText(anchor.text).length < 4) continue;
    paraTexts.forEach((t, i) => {
      if (!readerTextMatches(t, anchor.text)) return;
      const o = i - (anchor.bodyParagraphIndex as number);
      if (o >= 0) candidates.add(o);
    });
    break; // one anchor is enough to seed the candidate set
  }

  let best: number | null = null;
  let bestScore = 0;
  let bestTextTotal = 0;
  let bestTextHits = 0;
  for (const offset of Array.from(candidates).sort((a, b) => a - b)) {
    let score = 0;
    let textTotal = 0;
    let textHits = 0;
    for (const item of mapped) {
      const target = paraTexts[(item.bodyParagraphIndex as number) + offset];
      if (target === undefined) {
        score -= 2; // past the end of the document — this offset is wrong
        if (normalizeReaderText(item.text).length > 0) textTotal++;
        continue;
      }
      if (normalizeReaderText(item.text).length > 0) {
        textTotal++;
        const hit = readerTextMatches(target, item.text);
        if (hit) textHits++;
        score += hit ? 3 : -2;
      } else {
        score += normalizeReaderText(target).length === 0 ? 1 : 0;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestTextTotal = textTotal;
      bestTextHits = textHits;
      best = offset;
    }
  }
  // A positive score alone is not proof. On a 288-item spec an offset that is
  // wrong by a paragraph or two still scores well above zero on the blanks —
  // and a highlight on the WRONG paragraph is worse than no highlight, because
  // it invites the user to re-style someone else's line. Demand that most of
  // the items that actually carry text land on their own text; otherwise
  // return null and let the bar carry the review (`mappingLost`), where every
  // decision is still reachable and still targets the right paragraph.
  if (best != null && bestTextTotal > 0 && bestTextHits / bestTextTotal < MIN_TEXT_MATCH_RATIO) {
    return null;
  }
  return best;
}

/** How much of the flagged text must land on its own paragraph before an
    offset is trusted. Two-thirds: high enough to reject a shifted document,
    low enough to survive a handful of items whose text the formatter
    legitimately rewrote (smart title case, marker stripping). */
const MIN_TEXT_MATCH_RATIO = 2 / 3;

/**
 * Per-item degrade (table-adjacent dead-panel bug, 2026-09-03): the
 * document's offset is ONE join for every item, but a single item is allowed
 * to have rewritten text and still pass the file's overall ratio gate. Using
 * the global offset blindly for that item would highlight the WRONG
 * paragraph — worse than no highlight. This re-checks the ONE item against
 * its own computed position and returns null for it alone when the text
 * doesn't match, so the rest of the file keeps its highlights and this item
 * stays reachable (steppable, decidable) just without a visual ring.
 *
 * A blank item (empty-paragraph, page-break with no text) has nothing of its
 * own to verify — "is it blank" is already folded into the offset's own
 * scoring — so it trusts the file's offset like every other item did before
 * this function existed.
 */
export function itemRenderedIndex(
  item: Pick<DirtyItem, 'bodyParagraphIndex' | 'text'>,
  paraTexts: string[],
  offset: number | null
): number | null {
  if (offset == null || typeof item.bodyParagraphIndex !== 'number') return null;
  const idx = item.bodyParagraphIndex + offset;
  const target = paraTexts[idx];
  if (target === undefined) return null;
  if (normalizeReaderText(item.text).length > 0 && !readerTextMatches(target, item.text)) {
    return null;
  }
  return idx;
}

/**
 * Every top-level body paragraph docx-preview rendered, in document order and
 * across every section wrapper.
 *
 * docx-preview's shape is `div.docx-wrapper > section.docx > article > …`.
 * Headers and footers are <header>/<footer> siblings of the <article> and are
 * deliberately excluded — the engine's dirty scan never saw them. Tables come
 * out as <table> and are skipped too, because `bodyParagraphIndex` skips them.
 */
export function collectRenderedParagraphs(container: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const section of Array.from(container.querySelectorAll('section'))) {
    for (const child of Array.from(section.children)) {
      if (child.tagName !== 'ARTICLE') continue;
      for (const block of Array.from(child.children)) {
        if (block.tagName === 'P') out.push(block as HTMLElement);
      }
    }
  }
  return out;
}

/* ─── Post-fix preview (Fix 1, 2026-08-22) ──────────────────────────────
   A decided paragraph must immediately LOOK decided: the level's number in
   front of it, the level's indent under it, the level's weight and case.

   Geometry conversion is exact, not approximate. docx-preview converts every
   Word `Dxa` (twip) length with `mul: 0.05, unit: "pt"` and renders the page
   at 1:1 with no zoom or transform wrapper — so a twip value multiplied by
   0.05 and emitted in `pt` lands on the document's own ruler. Using px here
   would silently drift at any browser zoom; pt cannot.                    */

/** docx-preview's own Dxa scale — twips → points. */
export const TWIPS_TO_PT = 0.05;

export type PostFixPreview = {
  /** The number the level prints, e.g. "PART 1 -", "1.01", "A.". */
  prefix: string;
  /** Wrap-edge indent, in points. */
  indentPt: number;
  /** Width of the number column, in points. 0 ⇒ the number just flows. */
  hangingPt: number;
  bold: boolean;
  allCaps: boolean;
  fontSizePt?: number;
};

/** "Heading3" → 3. Anything else → null. */
export function headingLevelNumber(heading: string | undefined): number | null {
  const m = /^Heading([1-9])$/.exec(heading || '');
  return m ? Number(m[1]) : null;
}

/**
 * The post-fix look of a paragraph promoted to `heading`, or null when the
 * forma published no previews (older engine build) or the level is unknown.
 *
 * The prefix is the level's `sampleNumber` — the marker SHAPE at counter 1,
 * not this paragraph's eventual counter. Computing the real counter would mean
 * re-running the whole document's numbering against a decision map that is
 * still being edited (every new decision renumbers every later sibling), which
 * is exactly the re-format this reader exists to avoid. Shape it is.
 */
export function postFixPreview(
  heading: string | undefined,
  previews: LevelPreview[] | undefined
): PostFixPreview | null {
  const level = headingLevelNumber(heading);
  if (level == null || !previews || previews.length === 0) return null;
  const p = previews.find((x) => x.level === level);
  if (!p) return null;
  return {
    prefix: (p.sampleNumber || '').trim(),
    indentPt: (p.indentTwips || 0) * TWIPS_TO_PT,
    hangingPt: (p.hangingTwips || 0) * TWIPS_TO_PT,
    bold: !!p.bold,
    allCaps: !!p.allCaps,
    fontSizePt: p.fontSizePt,
  };
}

/* ─── Real sequence counters for the preview (Task 3, 2026-08-23) ───────────
   `sampleNumber` is the level's number at counter 1 — so a paragraph decided
   H4 directly under a native run of 1. … 6. previewed as "1." when Word will
   print "7.". Josh caught it on his own spec, and a preview that lies about
   the number is worse than no preview.

   The document's real numbering cannot be re-run here (that is the re-format
   this reader exists to avoid), but it does not need to be: the rendered DOM
   already declares every paragraph's outline level, and Word's counter rules
   are four lines of arithmetic. So the reader replays those rules over the
   levels it can see — the document's own styled/numbered paragraphs plus
   whatever the reviewer has decided in this session — and substitutes the
   result into the forma's sample number. */

/** docx-preview stamps every rendered paragraph with the numbering level it
    belongs to (`docx-num-<numId>-<ilvl>`) and/or its style (`docx_heading3`,
    lower-cased by the library). Numbering wins: it IS the outline level Word
    counts with. Null = this paragraph takes no number at all. */
export function renderedParagraphLevel(el: HTMLElement | null | undefined): number | null {
  const cls = el?.className;
  if (!cls || typeof cls !== 'string') return null;
  const num = /(?:^|\s)[\w-]*num-\d+-(\d+)(?:\s|$)/.exec(cls);
  if (num) {
    const level = Number(num[1]) + 1;
    if (level >= 1 && level <= 9) return level;
  }
  const heading = /(?:^|\s)[\w-]*heading-?([1-9])(?:\s|$)/i.exec(cls);
  return heading ? Number(heading[1]) : null;
}

/**
 * Word's outline counters, replayed over a document's levels.
 *
 * A paragraph at level L increments counter L and resets every deeper counter
 * — which is exactly "a level CONTINUES across its own siblings and after its
 * children, and RESETS when a shallower level intervenes". Paragraphs with no
 * level (body text, blanks, an undecided flagged line) are transparent: they
 * neither advance nor break a run.
 *
 * Returns the counter state AFTER each wanted index's own increment, so the
 * snapshot for a decided paragraph is that paragraph's own number. A document
 * whose levels are all unreadable leaves every counter at 0, which reads back
 * as 1 — the old counter-1 behaviour, never a guess.
 */
export function outlineCounters(
  levels: (number | null)[],
  wanted: Iterable<number>
): Map<number, number[]> {
  const want = new Set(wanted);
  const counters = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const out = new Map<number, number[]>();
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    if (level != null && level >= 1 && level <= 9) {
      counters[level - 1] += 1;
      for (let deeper = level; deeper < 9; deeper++) counters[deeper] = 0;
    }
    if (want.has(i)) out.set(i, counters.slice());
  }
  return out;
}

/** The Word numFmt a sample token was printed with, at counter 1. `01` can
    only be decimalZero, `A` only upperLetter, `I` only upperRoman, and so on —
    which is what makes the level's own sample enough to re-render it. */
function tokenNumFmt(token: string): string | null {
  if (/^0+1$/.test(token)) return 'decimalZero';
  if (token === '1') return 'decimal';
  if (token === 'A') return 'upperLetter';
  if (token === 'a') return 'lowerLetter';
  if (token === 'I') return 'upperRoman';
  if (token === 'i') return 'lowerRoman';
  return null;
}

/**
 * The forma's sample number for `level`, re-rendered at the real counters.
 *
 * The sample is generated with every counter at 1 ("PART 1 - ", "1.01", "A."),
 * so each counter token in it is one of a tiny known set and the tokens map
 * onto the levels ending at this one — "1.01" at level 2 is [level 1, level 2].
 * Anything that doesn't parse cleanly comes back untouched, at counter 1.
 */
export function numberFromSample(sample: string, level: number, counters: number[]): string {
  if (!sample) return sample;
  const tokens: { start: number; end: number; numFmt: string; level: number }[] = [];
  const word = /[A-Za-z0-9]+/g;
  let m: RegExpExecArray | null;
  while ((m = word.exec(sample)) !== null) {
    const numFmt = tokenNumFmt(m[0]);
    if (numFmt) tokens.push({ start: m.index, end: word.lastIndex, numFmt, level: 0 });
  }
  if (tokens.length === 0 || tokens.length > level) return sample;
  tokens.forEach((t, i) => {
    t.level = level - tokens.length + 1 + i;
  });
  let out = sample;
  // Right to left, so an earlier token's replacement can't shift a later one.
  for (const t of [...tokens].reverse()) {
    const value = Math.max(1, counters[t.level - 1] || 0);
    out = out.slice(0, t.start) + formatCounter(value, t.numFmt) + out.slice(t.end);
  }
  return out;
}

/** Every paragraph's pristine inline style, captured the first time the reader
    touches it. Undo/redecide restores from here, so no chain of decisions can
    ever leave the document rendering something Word wouldn't. */
const pristineStyle = new WeakMap<HTMLElement, string>();

const PREVIEW_CLASSES = [
  'sf-dirty--preview',
  'sf-dirty--preview-flow',
  'sf-dirty--preview-gone',
  'sf-dirty--preview-gone-break',
] as const;

/** Put a paragraph back exactly as docx-preview rendered it. */
export function clearPostFixPreview(el: HTMLElement): void {
  const original = pristineStyle.get(el);
  if (original !== undefined) el.style.cssText = original;
  el.classList.remove(...PREVIEW_CLASSES);
  el.removeAttribute('data-sf-prefix');
}

/**
 * Make the tint cover a HANGING first line (2026-08-23).
 *
 * Word's hanging indent renders as `margin-left: 36pt; text-indent: -18pt`, so
 * the first line — the one carrying the number — is painted OUTSIDE the
 * paragraph's own box, and the highlight stops 24px to its right. Every
 * auto-numbered CSI item in a real spec is shaped this way.
 *
 * The fix is the same identity `applyPostFixPreview` already uses: move the
 * hang out of the margin and into the padding. Margin-box left moves left by
 * the hang, padding-box left comes with it, and content-box left — therefore
 * every wrap point, and the first line's own start — does not move at all:
 *   before  margin 36 · indent −18  → line 1 at 18pt, wraps at 36pt
 *   after   margin 18 · padding 18 · indent −18 → line 1 at 18pt, wraps at 36pt
 * The used content WIDTH is unchanged too (margin loses exactly what padding
 * gains), so nothing re-wraps. Paint only, again.
 */
export function encloseHangingIndent(el: HTMLElement): void {
  const cs = getComputedStyle(el);
  const indent = parseFloat(cs.textIndent);
  if (!Number.isFinite(indent) || indent >= 0) return;
  const hang = -indent;
  const marginLeft = parseFloat(cs.marginLeft) || 0;
  const paddingLeft = parseFloat(cs.paddingLeft) || 0;
  if (!pristineStyle.has(el)) pristineStyle.set(el, el.style.cssText);
  el.style.marginLeft = `${marginLeft - hang}px`;
  el.style.paddingLeft = `${paddingLeft + hang}px`;
}

/**
 * Decorate one rendered paragraph into its post-fix state. Inline geometry
 * only — docx-preview writes the paragraph's own indent inline, so a class
 * could only win with `!important`, and restoring a captured `cssText` is
 * cheaper and more honest than fighting specificity.
 */
export function applyPostFixPreview(el: HTMLElement, preview: PostFixPreview): void {
  if (!pristineStyle.has(el)) pristineStyle.set(el, el.style.cssText);
  clearPostFixPreview(el);
  el.classList.add('sf-dirty--preview');
  if (preview.hangingPt <= 0) el.classList.add('sf-dirty--preview-flow');
  el.setAttribute('data-sf-prefix', preview.prefix);
  el.style.setProperty('--sf-fix-hang', `${preview.hangingPt}pt`);
  // Word's hanging indent, expressed so the paragraph's own BOX still contains
  // the number: the margin stops at the number column and the padding carries
  // the text to the wrap edge. Content-box left (and therefore the wrap point
  // of every line) is still exactly `indentPt`, while the tint — which paints
  // from the margin edge — now covers the number instead of leaving it
  // stranded outside the highlight.
  el.style.marginLeft = `${preview.indentPt - preview.hangingPt}pt`;
  el.style.paddingLeft = `${preview.hangingPt}pt`;
  el.style.textIndent = '0pt';
  el.style.fontWeight = preview.bold ? '700' : '400';
  el.style.textTransform = preview.allCaps ? 'uppercase' : 'none';
  if (typeof preview.fontSizePt === 'number') el.style.fontSize = `${preview.fontSizePt}pt`;
}

/**
 * A "Remove" decision, drawn for what it will ACTUALLY delete.
 *
 * - `'paragraph'` — the whole paragraph goes (a stray blank, or a page break
 *   that has a paragraph all to itself). Ghosted and struck through.
 * - `'break'` — only the manual page break riding on a real text paragraph
 *   goes. The TEXT survives, so striking it through would be a lie; the
 *   break's own formatting mark is struck instead, and any post-fix preview
 *   already on the paragraph is left alone.
 */
export function applyRemovedPreview(
  el: HTMLElement,
  scope: 'paragraph' | 'break' = 'paragraph'
): void {
  if (!pristineStyle.has(el)) pristineStyle.set(el, el.style.cssText);
  if (scope === 'break') {
    el.classList.add('sf-dirty--preview-gone-break');
    return;
  }
  clearPostFixPreview(el);
  el.classList.add('sf-dirty--preview-gone');
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
